import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { focusNextInputField } from 'components/UI/Inputs/AIQAutocomplete/Dropdown/Options/Options';

export function useDropdownBehavior({
    dropdownOpened,
    filteredOptions,
    aiSuggestions,
    value,
    inputValue,
    getInputValue,
    setInputValue,
    referenceExpr,
    setReferenceValue,
    onChange,
    setDropdownOpened,
    onAiSuggestionSelect,
}) {
    const dropdownRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [activeAiSuggestionIndex, setActiveAiSuggestionIndex] = useState(-1);
    const prevFilteredOptionsLengthRef = useRef(filteredOptions.length);
    const hasInitializedSelectionRef = useRef(false);
    const isKeyboardNavigatingRef = useRef(false);
    const aiCount = aiSuggestions?.length || 0;
    const totalCount = aiCount + filteredOptions.length;

    const currentValueLabel = useMemo(() => (value ? getInputValue(value) : ''), [value, getInputValue]);
    const inputMatchesCurrentValue = useMemo(() => inputValue === currentValueLabel, [inputValue, currentValueLabel]);

    const valueIndexInFilteredOptions = useMemo(() => {
        if (!value) return -1;
        return filteredOptions.findIndex((opt) => opt === value);
    }, [filteredOptions, value]);

    const applyViewportConstraint = useCallback(() => {
        const el = dropdownRef.current;
        if (!el || !dropdownOpened) return;

        el.style.left = '0';
        el.style.right = 'auto';

        requestAnimationFrame(() => {
            const current = dropdownRef.current;
            if (!current || current !== el || !el.isConnected) return;

            const rect = el.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const padding = 16;

            if (rect.right > viewportWidth - padding) {
                el.style.left = 'auto';
                el.style.right = '0';
            }
        });
    }, [dropdownOpened]);

    useEffect(() => {
        if (dropdownOpened) {
            applyViewportConstraint();
        }
    }, [dropdownOpened, applyViewportConstraint]);

    useEffect(() => {
        if (dropdownOpened) {
            applyViewportConstraint();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredOptions.length, aiSuggestions?.length]);

    const scrollToIndex = useCallback((optionIdx) => {
        if (optionIdx < 0) return;

        const rafId = requestAnimationFrame(() => {
            const container = dropdownRef.current;
            if (!container || !container.isConnected) return;

            const optionElements = container.querySelectorAll('[class*="option-container"]');
            const target = optionElements && optionElements[optionIdx];
            if (target && target.isConnected) {
                target.scrollIntoView({ block: 'nearest', behavior: 'auto' });
            }
        });

        return () => cancelAnimationFrame(rafId);
    }, []);

    useEffect(() => {
        if (!dropdownOpened) {
            setSelectedIndex(-1);
            return;
        }

        if (!inputValue) {
            setSelectedIndex(-1);
            if (dropdownRef.current) {
                dropdownRef.current.scrollTop = 0;
            }
            return;
        }

        if (inputMatchesCurrentValue && valueIndexInFilteredOptions >= 0) {
            setSelectedIndex(aiCount + valueIndexInFilteredOptions);
            scrollToIndex(valueIndexInFilteredOptions);
            return;
        }

        setSelectedIndex(-1);
        if (dropdownRef.current) {
            dropdownRef.current.scrollTop = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOpened]);

    useEffect(() => {
        if (!dropdownOpened) {
            hasInitializedSelectionRef.current = false;
            return;
        }

        if (isKeyboardNavigatingRef.current) {
            isKeyboardNavigatingRef.current = false;
            return;
        }

        const lengthChanged = prevFilteredOptionsLengthRef.current !== filteredOptions.length;
        const shouldSkip = !lengthChanged && hasInitializedSelectionRef.current && !inputValue;

        if (shouldSkip) {
            return;
        }

        prevFilteredOptionsLengthRef.current = filteredOptions.length;
        hasInitializedSelectionRef.current = true;

        if (inputMatchesCurrentValue && valueIndexInFilteredOptions >= 0) {
            setSelectedIndex(aiCount + valueIndexInFilteredOptions);
            setActiveAiSuggestionIndex(-1);
            return;
        }

        setSelectedIndex(-1);
        setActiveAiSuggestionIndex(-1);

        if (dropdownRef.current) {
            dropdownRef.current.scrollTop = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredOptions.length, dropdownOpened, inputValue]);

    useEffect(() => {
        if (dropdownOpened && !inputValue) {
            setSelectedIndex(-1);
        }
    }, [dropdownOpened, inputValue]);

    const stateRef = useRef({});
    stateRef.current = {
        selectedIndex,
        totalCount,
        aiCount,
        aiSuggestions,
        filteredOptions,
        onAiSuggestionSelect,
        getInputValue,
        setInputValue,
        referenceExpr,
        setReferenceValue,
        onChange,
        setDropdownOpened,
        setActiveAiSuggestionIndex,
    };

    useEffect(() => {
        if (!dropdownOpened || totalCount === 0) {
            setReferenceValue(-1);
            return;
        }

        function keydownHandler(e) {
            const s = stateRef.current;

            const applySelectedOption = () => {
                if (s.selectedIndex >= s.aiCount && s.selectedIndex < s.totalCount) {
                    const selectedOption = s.filteredOptions[s.selectedIndex - s.aiCount];
                    if (selectedOption) {
                        s.setInputValue(s.getInputValue(selectedOption));
                        if (s.referenceExpr) {
                            s.setReferenceValue(selectedOption[s.referenceExpr]);
                        }
                    }
                }
            };

            const updateInputForIndex = (index) => {
                if (index >= 0 && index < s.aiCount) {
                    const suggestion = s.aiSuggestions[index];
                    const displayValue = typeof suggestion.value === 'object' ? s.getInputValue(suggestion.value) : String(suggestion.value);
                    s.setInputValue(displayValue);
                    s.referenceExpr && s.setReferenceValue(-1);
                } else if (index >= s.aiCount) {
                    const option = s.filteredOptions[index - s.aiCount];
                    if (option) {
                        s.setInputValue(s.getInputValue(option));
                        s.referenceExpr && s.setReferenceValue(option[s.referenceExpr]);
                    }
                }
            };

            const scrollSelectedIntoView = (index) => {
                const optionIdx = index - s.aiCount;
                if (optionIdx >= 0) scrollToIndex(optionIdx);
            };

            switch (e.key) {
                case 'ArrowDown': {
                    e.preventDefault();
                    const newIndex = s.selectedIndex + 1 < s.totalCount ? s.selectedIndex + 1 : 0;
                    setSelectedIndex(newIndex);
                    isKeyboardNavigatingRef.current = true;
                    updateInputForIndex(newIndex);
                    scrollSelectedIntoView(newIndex);
                    break;
                }
                case 'ArrowUp': {
                    e.preventDefault();
                    const newIndex = s.selectedIndex - 1 >= 0 ? s.selectedIndex - 1 : s.totalCount - 1;
                    setSelectedIndex(newIndex);
                    isKeyboardNavigatingRef.current = true;
                    updateInputForIndex(newIndex);
                    scrollSelectedIntoView(newIndex);
                    break;
                }
                case 'Enter': {
                    if (s.selectedIndex >= 0 && s.selectedIndex < s.aiCount) {
                        s.setActiveAiSuggestionIndex(s.selectedIndex);
                        s.onAiSuggestionSelect(s.aiSuggestions[s.selectedIndex].value);
                    } else if (s.selectedIndex >= s.aiCount) {
                        s.setActiveAiSuggestionIndex(-1);
                        const option = s.filteredOptions[s.selectedIndex - s.aiCount];
                        if (option) {
                            s.onChange(null, option);
                            s.setDropdownOpened(false);
                        }
                    }
                    focusNextInputField(+e.target.id);
                    break;
                }
                case 'Tab': {
                    applySelectedOption();
                    if (!e.shiftKey) {
                        focusNextInputField(+e.target.id);
                    }
                    break;
                }
                default:
                    break;
            }
        }

        document.addEventListener('keydown', keydownHandler);
        return () => document.removeEventListener('keydown', keydownHandler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOpened, totalCount > 0]);

    useEffect(() => {
        const handleMouseDown = (event) => {
            if (dropdownRef.current?.contains(event.target)) {
                event.preventDefault();
                event.stopPropagation();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, []);

    const handleSetSelectedOptionIndex = useCallback(
        (optionIdx) => {
            setSelectedIndex(optionIdx >= 0 ? aiCount + optionIdx : -1);
        },
        [aiCount]
    );

    const handleAiSuggestionSelect = useCallback(
        (value, index) => {
            setActiveAiSuggestionIndex(index);
            onAiSuggestionSelect(value);
        },
        [onAiSuggestionSelect]
    );

    const handleOptionChange = useCallback(
        (e, option) => {
            setActiveAiSuggestionIndex(-1);
            onChange(e, option);
        },
        [onChange]
    );

    const selectedSuggestionIndex = selectedIndex >= 0 && selectedIndex < aiCount ? selectedIndex : -1;
    const selectedOptionIndex = selectedIndex >= aiCount ? selectedIndex - aiCount : -1;
    const hasAiSuggestions = aiCount > 0;
    const hasOptions = !!filteredOptions.length;

    return {
        dropdownRef,
        selectedSuggestionIndex,
        selectedOptionIndex,
        activeAiSuggestionIndex,
        hasAiSuggestions,
        hasOptions,
        handleSetSelectedOptionIndex,
        handleAiSuggestionSelect,
        handleOptionChange,
    };
}
